"use client";

import { useRef, useState } from "react";
import { EVENTS, referrerLabel, track } from "@/lib/analytics";

/**
 * One-click purchase-intent poll.
 *
 * There is no checkout to send anyone to, so the honest CTA on a pricing page
 * whose product is free is a question rather than a button. Each answer is an
 * anonymous Umami event — nothing is stored, no email is asked for, and the
 * copy says so plainly. The counts are the only signal we have about which
 * paid feature would be worth building first.
 *
 * "Rien, je ne paierais pas" is deliberately one of the options: without it the
 * result would only measure enthusiasm among people who already wanted to pay.
 */

const OPTIONS: { id: string; label: string }[] = [
  { id: "rappels", label: "Des rappels au moment de semer" },
  { id: "impression", label: "Un plan imprimable à emporter au jardin" },
  { id: "graines", label: "La liste des graines à acheter, calculée depuis mon plan" },
  { id: "historique", label: "Comparer mes récoltes d'une année sur l'autre" },
  { id: "parcelles", label: "Plusieurs potagers ou plusieurs parcelles" },
  { id: "aucun", label: "Rien de tout ça — je ne paierais pas" },
];

export function InterestPoll() {
  const [chosen, setChosen] = useState<string[]>([]);
  // Guarded on a ref rather than on `chosen`: a double click lands twice
  // before React re-renders, and the second one would count as a second vote.
  const tracked = useRef(new Set<string>());

  function choose(id: string) {
    if (tracked.current.has(id)) return;
    tracked.current.add(id);
    setChosen((prev) => [...prev, id]);
    track(EVENTS.pricingIntent, { feature: id, from: referrerLabel() });
  }

  return (
    <div className="max-w-2xl mx-auto mt-10">
      <h3 className="font-headline text-title-lg text-garden-800 text-center">
        Qu&apos;est-ce qui vous manquerait le plus&nbsp;?
      </h3>
      <p className="text-body-md text-on-surface-variant text-center mt-2 mb-6">
        Votre réponse décide de l&apos;ordre dans lequel ces fonctions seront
        construites. Un clic, c&apos;est tout : rien n&apos;est enregistré vous
        concernant, pas même votre adresse email.
      </p>

      <div className="flex flex-wrap justify-center gap-2.5">
        {OPTIONS.map((option) => {
          const selected = chosen.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={selected}
              onClick={() => choose(option.id)}
              className={`px-4 py-2.5 rounded-xl text-label-lg transition-colors ${
                selected
                  ? "bg-garden-800 text-on-primary"
                  : "bg-surface-container-lowest text-on-surface hover:bg-surface-container"
              }`}
            >
              {selected && (
                <span aria-hidden className="mr-1.5">
                  ✓
                </span>
              )}
              {option.label}
            </button>
          );
        })}
      </div>

      <p role="status" className="text-label-lg text-garden-700 text-center mt-6 min-h-[1.5rem]">
        {chosen.length > 0 && "Merci — c'est noté."}
      </p>
    </div>
  );
}
